<template>
  <v-dialog v-if="featureEnabled" :value="open" persistent width="546" @keydown.esc="handleClose">
    <v-card class="sjfld-card" data-testid="suggest-job-family-levels-dialog">
      <div class="sjfld-header">
        <span class="sjfld-featured-icon" aria-hidden="true" data-testid="sjfld-featured-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 3.5c.45 4.8 2.2 6.55 7 7-4.8.45-6.55 2.2-7 7-.45-4.8-2.2-6.55-7-7 4.8-.45 6.55-2.2 7-7Z"
              fill="#7f56d9"
            />
            <path
              d="M19 3c.16 1.7.78 2.32 2.5 2.5C19.78 5.68 19.16 6.3 19 8c-.16-1.7-.78-2.32-2.5-2.5C18.22 5.32 18.84 4.7 19 3Z"
              fill="#ed2738"
            />
          </svg>
        </span>
        <button
          type="button"
          class="sjfld-close"
          aria-label="Close Suggest dialog"
          data-testid="sjfld-close"
          @click="handleClose"
        >
          <svg
            class="sjfld-close__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <h2 class="sjfld-title" data-testid="sjfld-title">Suggest Job Family Levels</h2>
      </div>

      <div class="sjfld-body">
        <IQThinkingBanner
          :state="state"
          :reasoning="reasoning"
          :use-store-reasoning="false"
          :title="bannerTitle"
          :caption="bannerCaption"
          :phase="phase"
          :stalled="stalled"
          :collapsible="collapsible"
          :failed-title="FAILED_TITLE"
          :failed-message="FAILED_MESSAGE"
          :hide-retry="true"
        />
      </div>

      <div class="sjfld-actions">
        <template v-if="state === 'thinking'">
          <button
            type="button"
            class="sjfld-btn sjfld-btn--destructive"
            data-testid="sjfld-abort"
            @click="handleAbort"
          >
            Abort
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="sjfld-btn sjfld-btn--secondary"
            data-testid="sjfld-exit"
            :disabled="busy"
            @click="handleExit"
          >
            Exit
          </button>
          <button
            type="button"
            class="sjfld-btn sjfld-btn--destructive"
            data-testid="sjfld-retry"
            :disabled="busy"
            @click="handleRetry"
          >
            Retry
          </button>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SuggestJobFamilyLevelsDialog",
};
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11793 — SuggestJobFamilyLevelsDialog (Organization > Job Families > Levels
 * table header > Suggest). Modal shell for the IQ Suggest flow, hosting the shared
 * IQThinkingBanner in its two states:
 * - Thinking: title "Suggest Job Family Levels" + IQThinkingBanner Thinking state +
 *   a single destructive "Abort" action.
 * - Failed: the same shell swaps in IQThinkingBanner's Failed state, replacing
 *   "Abort" with "Exit" / "Retry". The banner's built-in retry is hidden
 *   (`hide-retry`) so the footer owns the single Retry action.
 *
 * Controlled, presentational: the parent owns `open`, drives `state` between
 * "thinking" and "failed", and receives `close` / `abort` / `retry` / `exit`. No
 * API call inside the component — the parent runs Suggest and flips the state.
 *
 * ER-12294 — the Thinking state now follows the Streamed AI Thinking Standard
 * lifecycle (Connecting → Streaming → Still working). The parent streams `phase`,
 * `reasoning` and `stalled` from the suggest SSE call; until the first stream event
 * the banner shows the static Connecting title/caption with skeleton lines, then
 * switches to the phase label, appending narrative and the collapse toggle.
 */
const THINKING_TITLE = "IQ is sourcing suggestions using context...";
const THINKING_CAPTION = "Analyzing sources...";
const FAILED_TITLE = "Suggestion failed";
const FAILED_MESSAGE = "We couldn't generate level suggestions. Retry, or Exit.";

const props = defineProps({
  /** Controls dialog visibility (v-model:open). */
  open: {
    type: Boolean,
    default: false,
  },
  /** Which IQThinkingBanner state to show: "thinking" or "failed". */
  state: {
    type: String,
    default: "thinking",
    validator: (value) => ["thinking", "failed"].includes(value),
  },
  /** Optional streamed reasoning lines to show while thinking (parent-provided). */
  reasoning: {
    type: String,
    default: "",
  },
  /** Current distilled phase label shown in the banner header (ER-12294). */
  phase: {
    type: String,
    default: "",
  },
  /** True when the stream's silence watchdog has fired (ER-12294). */
  stalled: {
    type: Boolean,
    default: false,
  },
  /** Enables collapse/expand of the reasoning body once streaming (ER-12294). */
  collapsible: {
    type: Boolean,
    default: false,
  },
  /**
   * True while a request is in flight. Disables the Failed-state Exit/Retry actions
   * (and the close ×) against double-fires; the Abort action (thinking state) is
   * intentionally never disabled, so the user can cancel the in-flight request
   * (ER-11835, ER-11833).
   */
  busy: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "abort", "retry", "exit"]);

// Connecting shows the static title + caption with skeleton lines; once the first
// stream event (phase or narrative) arrives the banner swaps to the phase label and
// streamed narrative, so the static Connecting copy is dropped (ER-12294).
const streamingStarted = computed(() => Boolean(props.phase) || Boolean(props.reasoning));

const bannerTitle = computed(() => (streamingStarted.value ? "" : THINKING_TITLE));
const bannerCaption = computed(() => (streamingStarted.value ? "" : THINKING_CAPTION));

const featureEnabled = ref(false);
const proxy = getCurrentInstance()?.proxy;

function handleClose() {
  if (props.busy) return;
  emit("close");
}

function handleAbort() {
  // ER-11835 / ER-11833 — Abort must fire even while busy: Abort stays enabled
  // while the request is in flight so the user can cancel the "thinking" state,
  // with no confirmation step. The parent's request sequence guard discards the
  // eventual response.
  emit("abort");
}

function handleRetry() {
  if (props.busy) return;
  emit("retry");
}

function handleExit() {
  if (props.busy) return;
  emit("exit");
}

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.sjfld-card {
  position: relative;
  background-color: #ffffff;
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
}

.sjfld-header {
  padding: 24px 24px 0;
}

.sjfld-featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.sjfld-close {
  position: absolute;
  top: 24px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #667085;
  cursor: pointer;
}

.sjfld-close:hover:not(:disabled) {
  background: #f2f4f7;
}

.sjfld-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.sjfld-title {
  margin: 0 0 16px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.sjfld-body {
  padding: 0 24px;
}

.sjfld-actions {
  display: flex;
  gap: 12px;
  padding: 24px;
}

.sjfld-btn {
  box-sizing: border-box;
  display: inline-flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.sjfld-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.sjfld-btn--destructive {
  background: #d92d20;
  border: 1px solid #d92d20;
  color: #ffffff;
}

.sjfld-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.sjfld-close:focus-visible,
.sjfld-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}
</style>
